import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Disbursement from 'components/global/Disbursement';
import { fetchAndSetDisbursement } from '../../../../global/Disbursement/Disbursement.helpers';
import { checkEdoProgramPermissions, shouldHaveFullEdoAccess } from '../../../../../libs/edoPermissions';
import edoFundingDisbursementSignal from '../../../../global/Disbursement/Disbursement.signals';
import loaderSignal from '../../../../../signals/Loader.signal';
import BreadCrumbWrapper from '../../../../global/BreadCrumbs/BreadCrumbs';
import userAccountSignal from '../../../../../signals/UserAccount.signal';

const EdoPerformanceDisbursement = () => {
  const location = useLocation();
  const { record } = edoFundingDisbursementSignal.value;
  const [canView, setCanView] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // On portfolio route we don't have fundingProgramId in URL, so we need to load the
  // disbursement/reimbursement first. Trigger fetch here so record (and thus fundingProgramId) gets set.
  useEffect(() => {
    if (
      !record &&
      (location.pathname.includes('disbursement') || location.pathname.includes('reimbursement'))
    ) {
      fetchAndSetDisbursement();
    }
  }, [location.pathname, record]);

  // Get funding program ID from URL if it's a performance route, otherwise from record
  const pathSegments = location.pathname.split('/');
  let fundingProgramId = null;

  if (location.pathname.includes('credit-program')) {
    // Performance route: /edo/performance/credit-program/:id/application/:applicationId/disbursement/:disbursementId
    const programIndex = pathSegments.indexOf('credit-program');
    if (programIndex !== -1 && pathSegments[programIndex + 1]) {
      fundingProgramId = Number(pathSegments[programIndex + 1]);
    }
  } else if (record) {
    // Portfolio route: get from loaded record
    const isReimbursement = !!record?.disbursementId;
    fundingProgramId = isReimbursement
      ? record?.disbursement?.application?.fundingProgramMembership?.fundingProgram?.id
      : record?.application?.fundingProgramMembership?.fundingProgram?.id;
  }

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      const userData = userAccountSignal.value.userData;
      
      // Wait for userData to be loaded
      if (!userData) {
        setIsChecking(true);
        return;
      }

      // If we have program ID from URL, check immediately
      if (fundingProgramId && !isNaN(fundingProgramId)) {
        try {
          setIsChecking(true);
          loaderSignal.update({ isContentLoading: true });
          const permissions = await checkEdoProgramPermissions(
            fundingProgramId,
            ['canView']
          );
          setCanView(permissions.canView);
          setIsChecking(false);
        } catch (error) {
          console.error('Error checking permissions:', error);
          setCanView(false);
          setIsChecking(false);
        } finally {
          loaderSignal.reset();
        }
      } else if (record) {
        // If we have record but no program ID yet, wait a bit and try again
        const isReimbursement = !!record?.disbursementId;
        const programId = isReimbursement
          ? record?.disbursement?.application?.fundingProgramMembership?.fundingProgram?.id
          : record?.application?.fundingProgramMembership?.fundingProgram?.id;

        if (programId) {
          try {
            setIsChecking(true);
            loaderSignal.update({ isContentLoading: true });
            const permissions = await checkEdoProgramPermissions(
              programId,
              ['canView']
            );
            setCanView(permissions.canView);
            setIsChecking(false);
          } catch (error) {
            console.error('Error checking permissions:', error);
            setCanView(false);
            setIsChecking(false);
          } finally {
            loaderSignal.reset();
          }
        } else {
          // No program ID available yet, check if user should have full access
          setCanView(shouldHaveFullEdoAccess(userData));
          setIsChecking(false);
        }
      } else {
        // No data yet, wait for it
        setIsChecking(true);
      }
    };

    checkPermissions();
  }, [fundingProgramId, record, userAccountSignal.value.userData]);

  if (isChecking && !record && !fundingProgramId) {
    return (
      <BreadCrumbWrapper labels={['']}>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <p className="text-center">Loading...</p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  if (!isChecking && !canView) {
    return (
      <BreadCrumbWrapper labels={['']}>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <h3 className="text-center">Access Denied</h3>
          <p className="text-center">
            You do not have permission to view this disbursement.
          </p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  return <Disbursement />;
};

export default EdoPerformanceDisbursement;
